import {
    ClockIcon,
    ExclamationTriangleIcon,
    TruckIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Employee monitoring across every agency.
 *
 * Employees list cars on their agent's behalf - buyers only ever see the
 * agency name. This page is where the platform owner can see who is actually
 * doing the work.
 */

const ACTION_LABEL = {
  login: 'Signed in',
  logout: 'Signed out',
  car_listed: 'Listed a car',
  change_requested: 'Requested a change',
  chat_replied: 'Replied to a buyer',
};

const ACTION_COLOUR = {
  login: 'bg-green-100 text-green-700',
  logout: 'bg-gray-100 text-gray-600',
  car_listed: 'bg-orange-100 text-orange-700',
  change_requested: 'bg-blue-100 text-blue-700',
  chat_replied: 'bg-purple-100 text-purple-700',
};

const fullName = (p) =>
  p ? [p.firstName, p.lastName].filter(Boolean).join(' ') : '\u2014';

const agencyName = (a) =>
  a ? (a.businessName || fullName(a)) : 'Unassigned';

const EmployeeMonitoring = () => {
  const [employees, setEmployees] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentFilter, setAgentFilter] = useState('all');

  const auth = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const base = process.env.REACT_APP_API_URL;
      const [emp, act] = await Promise.all([
        axios.get(`${base}/api/employees`, auth()),
        axios.get(`${base}/api/employees/activity?limit=200`, auth()),
      ]);
      setEmployees(Array.isArray(emp.data?.data) ? emp.data.data : []);
      setActivity(Array.isArray(act.data?.data) ? act.data.data : []);
    } catch (error) {
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  // Agencies that actually have staff, for the filter.
  const agencies = [];
  const seen = new Set();
  employees.forEach((e) => {
    const id = e.agentId?._id;
    if (id && !seen.has(String(id))) {
      seen.add(String(id));
      agencies.push({ id: String(id), name: agencyName(e.agentId) });
    }
  });

  const shownEmployees = agentFilter === 'all'
    ? employees
    : employees.filter((e) => String(e.agentId?._id) === agentFilter);

  const shownActivity = agentFilter === 'all'
    ? activity
    : activity.filter((a) => String(a.agent?._id) === agentFilter);

  const totalListed = employees.reduce((s, e) => s + (e.stats?.total || 0), 0);
  const activeStaff = employees.filter((e) => e.isActive).length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employee Monitoring</h1>
          <p className="text-gray-600 mt-1">
            Staff listing cars on behalf of their agency
          </p>
        </div>

        {agencies.length > 1 && (
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All agencies</option>
            {agencies.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Employees</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{employees.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{activeStaff}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Cars Listed</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{totalListed}</p>
        </div>
      </div>

      {employees.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <UserGroupIcon className="w-10 h-10 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-3">
            No agency has added employees yet.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center">
            <UserGroupIcon className="w-5 h-5 text-gray-500 mr-2" />
            <h2 className="font-semibold text-gray-900">Staff</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Employee</th>
                  <th className="text-left px-5 py-3 font-semibold">Agency</th>
                  <th className="text-right px-5 py-3 font-semibold">Listed</th>
                  <th className="text-right px-5 py-3 font-semibold">Active</th>
                  <th className="text-right px-5 py-3 font-semibold">Sold</th>
                  <th className="text-left px-5 py-3 font-semibold">Last Seen</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shownEmployees.map((e) => (
                  <tr key={e._id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900">{fullName(e)}</p>
                      <p className="text-xs text-gray-500">
                        {e.email}
                        {e.employeeProfile?.designation
                          ? ` · ${e.employeeProfile.designation}`
                          : ''}
                      </p>
                    </td>
                    <td className="px-5 py-3 text-gray-700">{agencyName(e.agentId)}</td>
                    <td className="px-5 py-3 text-right font-medium">{e.stats?.total || 0}</td>
                    <td className="px-5 py-3 text-right text-blue-600">{e.stats?.active || 0}</td>
                    <td className="px-5 py-3 text-right text-green-600">{e.stats?.sold || 0}</td>
                    <td className="px-5 py-3 text-gray-600">
                      {e.employeeProfile?.lastLoginAt
                        ? formatDistanceToNow(new Date(e.employeeProfile.lastLoginAt), { addSuffix: true })
                        : 'Never'}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                        e.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {e.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity feed */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <ClockIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h2 className="font-semibold text-gray-900">Recent Activity</h2>
        </div>

        <div className="flex items-start bg-amber-50 border-b border-amber-100 px-6 py-3">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 ml-2">
            Sign-outs are only recorded when the app closes properly, so a
            missing one does not mean the person is still working.
          </p>
        </div>

        {shownActivity.length === 0 ? (
          <div className="py-12 text-center text-gray-500 text-sm">
            No activity recorded yet.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
            {shownActivity.map((a) => (
              <motion.div
                key={a._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-6 py-3 flex items-center gap-4"
              >
                <span className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${
                  ACTION_COLOUR[a.action] || 'bg-gray-100 text-gray-600'
                }`}>
                  {ACTION_LABEL[a.action] || a.action}
                </span>

                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    {fullName(a.employee)}
                    <span className="text-gray-400"> · </span>
                    <span className="text-gray-600">{agencyName(a.agent)}</span>
                  </p>
                  {a.detail && (
                    <p className="text-xs text-gray-500 truncate">{a.detail}</p>
                  )}
                </div>

                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {format(new Date(a.createdAt), 'd MMM, HH:mm')}
                </span>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-start bg-gray-50 border border-gray-200 rounded-lg p-4">
        <TruckIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-600 ml-3">
          Listings created by an employee belong to their agency and appear to
          buyers under the agency name. Employees only see their own listings;
          their agent sees everything.
        </p>
      </div>
    </div>
  );
};

export default EmployeeMonitoring;
